import api from './api';

export interface FooterLink {
  id?: number;
  label: string;
  url: string;
  order: number;
  is_active: boolean;
  opens_new_tab: boolean;
}

export interface FooterSection {
  id?: number;
  title: string;
  slug: string;
  order: number;
  is_active: boolean;
  links: FooterLink[];
}

export interface SocialLink {
  id?: number;
  platform: string;
  url: string;
  icon_name: string;
  order: number;
  is_active: boolean;
}

export interface FooterConfig {
  sections: FooterSection[];
  social_links: SocialLink[];
  site_info: Record<string, string>;
}

export interface WhiteLabelConfig {
  id: number;
  name: string;
  domain: string;
  branding_config: {
    logo_url?: string;
    primary_color?: string;
    secondary_color?: string;
    theme?: string;
  };
  feature_flags: {
    auctions_enabled?: boolean;
    games_enabled?: boolean;
    streaming_enabled?: boolean;
    otc_enabled?: boolean;
    dating_dates_enabled?: boolean;
  };
}

class SiteConfigService {
  async getWhiteLabelConfig(): Promise<WhiteLabelConfig> {
    const response = await api.get('/whitelabel/config');
    return response.data;
  }

  async getFooterConfig(): Promise<FooterConfig> {
    const response = await api.get('/api/site-config/footer');
    return response.data;
  }

  async getPublicSettings(): Promise<Record<string, string>> {
    const response = await api.get('/api/site-config/settings/public');
    return response.data;
  }

  // Admin endpoints
  async getAllFooterSections(): Promise<FooterSection[]> {
    const response = await api.get('/api/site-config/admin/footer-sections');
    return response.data;
  }

  async createFooterSection(section: Partial<FooterSection>): Promise<FooterSection> {
    const response = await api.post('/api/site-config/admin/footer-sections', section);
    return response.data;
  }

  async updateFooterSection(id: number, section: Partial<FooterSection>): Promise<FooterSection> {
    const response = await api.put(`/api/site-config/admin/footer-sections/${id}`, section);
    return response.data;
  }

  async deleteFooterSection(id: number): Promise<void> {
    await api.delete(`/api/site-config/admin/footer-sections/${id}`);
  }

  async createFooterLink(sectionId: number, link: Partial<FooterLink>): Promise<FooterLink> {
    const response = await api.post(`/api/site-config/admin/footer-links?section_id=${sectionId}`, link);
    return response.data;
  }

  async updateFooterLink(id: number, link: Partial<FooterLink>): Promise<FooterLink> {
    const response = await api.put(`/api/site-config/admin/footer-links/${id}`, link);
    return response.data;
  }

  async deleteFooterLink(id: number): Promise<void> {
    await api.delete(`/api/site-config/admin/footer-links/${id}`);
  }

  async getAllSocialLinks(): Promise<SocialLink[]> {
    const response = await api.get('/api/site-config/admin/social-links');
    return response.data;
  }

  async createSocialLink(link: Partial<SocialLink>): Promise<SocialLink> {
    const response = await api.post('/api/site-config/admin/social-links', link);
    return response.data;
  }

  async updateSocialLink(id: number, link: Partial<SocialLink>): Promise<SocialLink> {
    const response = await api.put(`/api/site-config/admin/social-links/${id}`, link);
    return response.data;
  }

  async deleteSocialLink(id: number): Promise<void> {
    await api.delete(`/api/site-config/admin/social-links/${id}`);
  }

  async getAllSettings(): Promise<Record<string, any>> {
    const response = await api.get('/api/site-config/admin/settings');
    return response.data;
  }

  async updateSetting(key: string, value: string): Promise<void> {
    await api.put(`/api/site-config/admin/settings/${key}?value=${encodeURIComponent(value)}`);
  }
}

export default new SiteConfigService();
